import { useState } from 'react';

/**
 * Part-to-whole as a ring.
 *
 * Extracted from the assistant's answer cards so the Time Tracker dashboard can
 * draw the same chart rather than grow a second one that hovers, gaps and
 * collapses its tail slightly differently. It knows nothing about either
 * caller's data shape — both map their rows to {@link DonutSlice} at the call
 * site.
 */

/** The categorical slots, in fixed order. A seventh category never gets a hue. */
const SERIES = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
];

/** How many slices a donut may carry before the tail is folded into "Other". */
const DONUT_MAX = 6;

export interface DonutSlice {
  name: string;
  value: number;
  /**
   * What selecting this slice would mean. Only slices carrying one are
   * selectable, so a caller that cannot say which subject a slice stands for —
   * two rows sharing a name, say — simply omits it and the slice renders as
   * plain text rather than as a keyboard stop that does nothing.
   */
  id?: string;
  /**
   * The subject's own colour, where it has one — a project's colour, not its
   * rank in the list, so the same project is the same hue on every screen and
   * in every period. Falls back to the categorical palette.
   */
  color?: string;
}

interface DonutProps {
  slices: DonutSlice[];
  unit?: string;
  /** What the ring is a share *of* — the one figure percentages cannot carry. */
  centre?: { value: string; label: string };
  /**
   * How a value is written out. Defaults to the bare number, which is what a
   * count wants; a caller measuring in hours passes the same formatter it uses
   * for the numbers printed beside the chart, so one figure is never spelled
   * two ways on one screen.
   */
  format?: (value: number) => string;
  /**
   * Makes id-carrying segments and their legend rows selectable, called with
   * the slice's id. The collapsed "Other (N)" tail stands for several subjects
   * at once and never fires.
   */
  onSelect?: (id: string) => void;
}

/**
 * A donut earns its place only for composition read at a glance, and only up to
 * six segments — past that, slices of similar size become indistinguishable and
 * the ring says less than a list would. So the tail is folded into "Other"
 * rather than drawn: ten projects at 14/12/11/10/10/9/9/8/8/7 per cent is the
 * exact case a pie chart is famous for ruining.
 *
 * Every segment is direct-labelled in the legend beside it, which is also what
 * makes the palette legible without relying on colour alone.
 */
export function Donut({ slices: input, unit, centre, format = String, onSelect }: DonutProps) {
  // Declared before any early return: a hook behind a condition changes the
  // hook order between renders, and a chart whose data empties out would throw
  // rather than simply disappear.
  const [active, setActive] = useState<number | null>(null);

  const rows = input.filter((r) => r.value > 0);
  if (rows.length === 0) return null;

  const slices: DonutSlice[] =
    rows.length > DONUT_MAX
      ? [
          ...rows.slice(0, DONUT_MAX - 1),
          {
            // No id: the tail stands for several subjects at once, so selecting
            // it would have to mean one of them.
            name: `Other (${rows.length - DONUT_MAX + 1})`,
            value: rows.slice(DONUT_MAX - 1).reduce((sum, r) => sum + r.value, 0),
          },
        ]
      : rows;

  const total = slices.reduce((sum, s) => sum + s.value, 0) || 1;
  // A stroked circle, drawn by dash offsets: no arc maths, and the 2px gap
  // between segments falls out of the dash array for free.
  const R = 60;
  const CIRCUMFERENCE = 2 * Math.PI * R;
  let offset = 0;

  const shown = active !== null ? slices[active] : null;
  const colourOf = (slice: DonutSlice, i: number) => slice.color ?? SERIES[i % SERIES.length];
  /** Only a slice that knows what it stands for can be selected. */
  const idOf = (slice: DonutSlice): string | null => (onSelect && slice.id ? slice.id : null);

  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-3"
      onMouseLeave={() => setActive(null)}
    >
      <div className="relative shrink-0">
        <svg viewBox="0 0 160 160" className="h-[132px] w-[132px]" role="img">
          <g transform="rotate(-90 80 80)">
            {slices.map((s, i) => {
              const length = (s.value / total) * CIRCUMFERENCE;
              const dash = `${Math.max(length - 2, 0)} ${CIRCUMFERENCE - Math.max(length - 2, 0)}`;
              const id = idOf(s);
              const el = (
                <circle
                  key={i}
                  cx="80"
                  cy="80"
                  r={R}
                  fill="none"
                  stroke={colourOf(s, i)}
                  // The hovered segment thickens rather than changing colour:
                  // a colour shift would read as a different category.
                  strokeWidth={active === i ? 24 : 18}
                  strokeDasharray={dash}
                  strokeDashoffset={-offset}
                  className={
                    id
                      ? 'cursor-pointer transition-[stroke-width] duration-150'
                      : 'cursor-default transition-[stroke-width] duration-150'
                  }
                  style={{ opacity: active === null || active === i ? 1 : 0.45 }}
                  onMouseEnter={() => setActive(i)}
                  onClick={id ? () => onSelect?.(id) : undefined}
                >
                  <title>{`${s.name}: ${format(s.value)}${unit ?? ''}`}</title>
                </circle>
              );
              offset += length;
              return el;
            })}
          </g>
        </svg>
        {/* The hole is the readout: the total at rest, the hovered slice while
            pointing at one. A ring with an empty middle wastes its best space. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <span className="text-base font-semibold tabular-nums leading-none text-foreground">
            {shown
              ? `${format(shown.value)}${unit ?? ''}`
              : (centre?.value ?? `${Math.round(total)}${unit ?? ''}`)}
          </span>
          <span className="mt-0.5 line-clamp-2 text-[9px] leading-tight text-muted-foreground">
            {shown ? shown.name : (centre?.label ?? 'total')}
          </span>
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-0.5">
        {slices.map((s, i) => {
          const id = idOf(s);
          return (
            <li key={i}>
              {/* A button only where there is something to select, so a keyboard
                  reader is never offered a stop that does nothing. */}
              {id ? (
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => onSelect?.(id)}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1 py-0.5 text-left text-[11px] transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                >
                  <LegendRow slice={s} colour={colourOf(s, i)} unit={unit} format={format} />
                </button>
              ) : (
                <span
                  onMouseEnter={() => setActive(i)}
                  className="flex cursor-default items-center gap-2 rounded-md px-1 py-0.5 text-[11px] transition-colors hover:bg-muted/50"
                >
                  <LegendRow slice={s} colour={colourOf(s, i)} unit={unit} format={format} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The swatch, name and value of one legend entry — identical whether or not
 *  the row is selectable, so the two variants cannot drift apart. */
function LegendRow({
  slice,
  colour,
  unit,
  format,
}: {
  slice: DonutSlice;
  colour: string;
  unit?: string;
  format: (value: number) => string;
}) {
  return (
    <>
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colour }} />
      <span className="min-w-0 flex-1 truncate text-muted-foreground">{slice.name}</span>
      <span className="shrink-0 font-medium tabular-nums text-foreground">
        {format(slice.value)}
        {unit ?? ''}
      </span>
    </>
  );
}
